import React, { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { fromIso, monthKey, pad, todayIso, toIso } from './dates';
import { IconLeft, IconRight } from './Icons';
import { PaperInfo } from './types';

interface Props {
  selected: string | null;
  version: number; // bump to reload after an upload / delete
  load?: (month: string) => Promise<PaperInfo[]>; // defaults to the signed-in listing
  onPick: (date: string, paper: PaperInfo | null) => void;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function Calendar({ selected, version, onPick, load }: Props) {
  const [cursor, setCursor] = useState<Date>(() => {
    const d = selected ? fromIso(selected) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [papers, setPapers] = useState<Record<string, PaperInfo>>({});
  const [loading, setLoading] = useState(false);
  const today = todayIso();

  // follow the selected date when it moves to another month
  useEffect(() => {
    if (!selected) return;
    const d = fromIso(selected);
    setCursor((c) => (c.getFullYear() === d.getFullYear() && c.getMonth() === d.getMonth() ? c : new Date(d.getFullYear(), d.getMonth(), 1)));
  }, [selected]);

  const key = monthKey(cursor);
  useEffect(() => {
    let live = true;
    setLoading(true);
    (load ? load(key) : api.month(key))
      .then((list) => {
        if (!live) return;
        const m: Record<string, PaperInfo> = {};
        list.forEach((p) => (m[p.date] = p));
        setPapers(m);
      })
      .catch(() => live && setPapers({}))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [key, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7; // Monday first
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const out: (string | null)[] = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= days; d++) out.push(`${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(d)}`);
    while (out.length % 7) out.push(null);
    return out;
  }, [cursor]);

  const move = (n: number) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  const count = Object.keys(papers).length;

  return (
    <div className="cal">
      <div className="cal-head">
        <button className="icon-btn" onClick={() => move(-1)} aria-label="Previous month">
          <IconLeft />
        </button>
        <div className="cal-title">
          <strong>{cursor.toLocaleDateString(undefined, { month: 'long' })}</strong>
          <span>{cursor.getFullYear()}</span>
        </div>
        <button className="icon-btn" onClick={() => move(1)} aria-label="Next month">
          <IconRight />
        </button>
      </div>
      <div className="cal-grid" role="grid" aria-busy={loading}>
        {DOW.map((d) => (
          <div key={d} className="dow">
            {d}
          </div>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <div key={'e' + i} />;
          const has = !!papers[iso];
          const future = iso > today;
          const cls = ['day', has ? 'has' : '', iso === today ? 'today' : '', iso === selected ? 'sel' : ''].join(' ');
          return (
            <button
              key={iso}
              className={cls}
              disabled={future}
              onClick={() => onPick(iso, papers[iso] || null)}
              title={has ? `${papers[iso].pageCount} pages` : future ? '' : 'No paper yet – tap to upload'}
              aria-label={fromIso(iso).toDateString() + (has ? ', newspaper available' : ', no newspaper')}
            >
              {Number(iso.slice(8))}
              {has && <i className="dot" />}
            </button>
          );
        })}
      </div>
      <div className="cal-foot">
        <span>
          <i className="dot inline" /> Paper available
        </span>
        <button className="link" onClick={() => onPick(today, papers[today] || null)}>
          Today
        </button>
      </div>
      <p className="muted small">{loading ? 'Loading…' : `${count} paper${count === 1 ? '' : 's'} this month`}</p>
    </div>
  );
}

export { toIso };
